import React from 'react';
import Imgix from 'react-imgix';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import ProductBadge from '../../../common/ProductBadge';
import {
  checkIsRug,
  getImgixUrl,
  getName,
  getPrice
} from '../../../utils/helper';
import Heart from '../common/greyHeart/Heart';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  background: white;
  display: flex;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ index }) =>
    index === 0 &&
    css`
      grid-row: 1 / 1;
      grid-column: 1 / 1;
    `}

  .image-container {
    position: relative;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 56.25%;
    a {
      position: absolute;
      top: 0;
      left: 0;
      background: #f7f9fa;
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      .product-image {
        position: absolute;
        max-width: 100%;
        max-height: 100%;
        width: auto;
      }
    }
    .heart-container {
      z-index: 1;
      position: absolute;
      right: 4px;
      top: 4px;
    }

    .new-badge-container {
      z-index: 1;
      position: absolute;
      left: 10px;
      top: 13px;
    }
  }
  .description-container {
    padding: 10px 0;
    background: #F8F8F8;

    .title {
      display: flex;
      justify-content: center;
    }

    .fabric-name {
      margin: 10px 0;
      display: flex;
      flex-direction: column;
    }
  }

  .collab {
    margin-top: 3px;
    display: flex;
    text-align: center;
  }

  .size-options-container {
    display: flex;
    justify-content: center;
  }
  .size-options {
    text-align: center;
    margin-top: 5px;
    font-size: 14px;
    line-height: 17px;
    color: ${({ theme }) => theme.colors.charcoal};

    &:hover {
      text-decoration: none;
    }
  }

  @media (max-width: 992px) {
    background: #F8F8F8;
    padding-top: 20px;
    .image-container {
      .heart-container {
        left: 4px;
        top: 3px;
      }

      .new-badge-container {
        right: 9px;
        top: 0px;
      }

      a {
        .product-image {
          /* width: 100%; */
          height: auto;
          position: initial;
        }
      }
    }
    .description-container {
      .title {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin-bottom: 8px;
      }

      .fabric-name {
        padding: 0 5px;
        display: block;
        flex-direction: column;
        margin-bottom: 8px;
        justify-content: center;
        text-align: center;
      }
    }
  }
`;

function Product({
  id,
  fabric,
  name,
  price,
  fullPrice,
  image,
  slug,
  index,
  isCollections,
  merchandiserId,
  fullName,
  product,
  showPriceRange
}) {
  price = parseFloat(price);
  fullPrice = parseFloat(fullPrice);

  const withParamsUrl = `/products/${slug}?collection_id=${merchandiserId}`;
  const calculateSavings = (price, fullPrice) => {
    return parseInt(Math.round(((fullPrice - price) / fullPrice) * 100));
  };

  const isRug = checkIsRug(name);

  const getPriceRange = (minPrice, maxPrice) => {
    return `$${getPrice(minPrice)} - $${getPrice(maxPrice)}`;
  };

  const isWallpaper = product?.frame?.type?.id == 4;

  const isFabricByTheYard = [162].includes(parseInt(product?.frame?.id));

  const priceText = isWallpaper ? (
    <span className="roll-txt"> / roll</span>
  ) : isFabricByTheYard ? (
    <span className="roll-txt"> / yard</span>
  ) : (
    ''
  );

  return (
    <Wrapper className="product-wrapper" index={index}>
      {product?.badge_treatment && (
        <ProductBadge
          badgeTreatment={product.badge_treatment}
          leftAlignedWithPadding={true}
        />
      )}
      <div className="image-container">
        <div className="heart-container hide-on-mobile">
          <Heart productName={name} productSlug={slug} productId={id} />
        </div>
        <Link to={isCollections ? withParamsUrl : `/products/${slug}`}>
          <Imgix
            className="product-image"
            imgixParams={{ auto: 'compress,format' }}
            sizes="
            (min-width: 1301px) 25vw,
            (min-width: 993px) 33vw,
            45vw"
            src={getImgixUrl(image)}
            htmlAttributes={{
              alt: name,
              loading: 'lazy'
            }}
          />
        </Link>
      </div>
      <div className="description-container">
        <div className="title">
          <ProductTitle className="product-title">
            {fullName ? fullName : getName(name)}
          </ProductTitle>
        </div>
        {fabric && (
          <FabricName className="fabric-name">
            <div className="fabric-name-text">in {fabric.name} </div>
            {fabric.collaborator && (
              <>
                {' '}
                <span className="collab-mobile">
                  {' '}
                  by {fabric.collaborator.name}
                </span>
              </>
            )}
          </FabricName>
        )}
        <ProductPrice className="price">
          {price < fullPrice ? (
            <>
              <FullPrice>${getPrice(fullPrice)}</FullPrice>
              <Price>
                ${getPrice(price)}
                {priceText}
              </Price>
              <Savings>
                (Save {calculateSavings(price, fullPrice)}
                %)
              </Savings>
            </>
          ) : showPriceRange ? (
            getPriceRange(product?.min_price, product?.max_price)
          ) : (
            <>
              ${getPrice(price)}
              {priceText}
            </>
          )}
        </ProductPrice>
        {isRug && (
          <div className="size-options-container">
            <BodyFourLink
              className="size-options"
              to={isCollections ? withParamsUrl : `/products/${slug}`}
            >
              See All Size Options
            </BodyFourLink>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

export default Product;

const BodyFourLink = styled(Link)``;

const ProductTitle = styled.div`
  font-family: ${({ theme }) => theme.old ? theme.fonts.primary : theme.fonts.secondary.family};
  letter-spacing: ${({ theme }) => theme.old ? '0.04em' : theme.fonts.secondary.letterSpacing};
  font-style: normal;
  font-weight: ${({ theme }) => theme.old ? 'bold' : 400};
  font-size: ${({ theme }) => theme.old ? '16px' : '18px'};
  line-height: 23px;
  /* identical to box height, or 144% */

  display: flex;
  align-items: center;
  text-align: center;

  color: ${({ theme }) => theme.colors.charcoal};
  @media (max-width: 992px) {
    font-family: ${({ theme }) => theme.fonts.primary};
    font-style: normal;
    font-weight: ${({ theme }) => theme.old ? 'bold' : 600};
    font-size: 14px;
    line-height: 17px;
  }
`;

const ProductPrice = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => theme.old ? 'bold' : 600};
  font-size: 15px;
  line-height: 18px;
  /* identical to box height, or 144% */

  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  letter-spacing: 0.04em;

  color: ${({ theme }) => theme.old ? '#727272' : theme.colors.charcoal};
  @media (max-width: 992px) {
    display: inherit;
    font-size: 13px;
    line-height: 16px;
  }
`;

const FullPrice = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-right: 12px;
  text-decoration-line: line-through;
  color: ${({ theme }) => theme.old ? '#727272' : theme.colors.charcoal};
  @media (max-width: 991px) {
    font-size: 13px;
    line-height: 16px;
    margin-right: 0;
    display: block;
  }
`;

const Price = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => theme.old ? 'bold' : 600};
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.french};
  @media (max-width: 991px) {
    font-size: 13px;
    line-height: 16px;
    display: block;
    margin-top: 5px;
    margin-bottom: 5px;
  }
`;

const Savings = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-size: 15px;
  line-height: 18px;
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.french};
  opacity: 0.9;
  @media (max-width: 991px) {
    font-size: 13px;
    line-height: 16px;
    display: block;
    margin-left: 0;
  }
`;

const FabricName = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => theme.old ? '500' : '400'};
  font-size: 14px;
  line-height: 17px;
  /* identical to box height, or 144% */

  display: flex;
  align-items: center;
  letter-spacing: 0.04em;

  color: ${({ theme }) => theme.colors.charcoal};
  @media (max-width: 992px) {
    display: block;
    font-size: 13px;
    line-height: 16px;

    span {
      display: inline;
    }

    .fabric-name-text {
      display: inline;
    }
  }
`;
